/**
 * XRCertificateVerifyPage — v10.8.4 (public)
 * Página pública para verificar autenticidad de un certificado XR escaneado
 * desde su QR. La URL es `/xr/certificate/verify/:id?sig=...`.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, WarningCircle, Trophy } from '@phosphor-icons/react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function XRCertificateVerifyPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sig = searchParams.get('sig') || '';
  const [state, setState] = useState('loading');
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/xr/certificates/verify/${id}${sig ? `?sig=${encodeURIComponent(sig)}` : ''}`)
      .then((r) => {
        setData(r.data);
        setState(r.data?.valid ? 'valid' : 'invalid');
      })
      .catch(() => setState('error'));
  }, [id, sig]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-cyan-900 flex items-center justify-center p-4" data-testid="cert-verify-page">
      <Card className={`w-full max-w-md ${state === 'valid' ? 'bg-white' : 'bg-red-50 border-red-200'}`}>
        <CardContent className="p-8 text-center space-y-4">
          {state === 'loading' && <div className="text-slate-500">Verificando certificado…</div>}

          {state === 'valid' && data && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle size={44} className="text-emerald-600" weight="fill" />
              </div>
              <div>
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-1">Certificado válido</Badge>
                <h1 className="text-2xl font-bold text-slate-900 mt-1">SimuClinic XR</h1>
              </div>
              <div className="border-t border-slate-100 pt-4 text-left space-y-2">
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Otorgado a</div>
                  <div className="text-xl font-bold text-slate-900" data-testid="cert-user-name">{data.user_name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-400 tracking-widest">Escenario</div>
                  <div className="text-sm font-medium text-slate-700" data-testid="cert-scenario-name">{data.scenario_name}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <div className="text-center p-2 bg-cyan-50 rounded">
                    <div className="text-[9px] uppercase text-cyan-600 font-bold">Precisión</div>
                    <div className="text-lg font-bold text-cyan-700">{data.accuracy_pct}%</div>
                  </div>
                  <div className="text-center p-2 bg-emerald-50 rounded">
                    <div className="text-[9px] uppercase text-emerald-600 font-bold">Umbral</div>
                    <div className="text-lg font-bold text-emerald-700">{data.threshold}%</div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 mt-2 text-center">
                  Emitido {data.issued_at?.slice(0, 10)} · ID <span className="font-mono">{data.certificate_id?.slice(0, 8)}…</span>
                </div>
              </div>
            </>
          )}

          {(state === 'invalid' || state === 'error') && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <WarningCircle size={44} className="text-red-600" weight="fill" />
              </div>
              <div>
                <Badge className="bg-red-100 text-red-700 border-red-200 mb-1">Certificado no válido</Badge>
                <h1 className="text-2xl font-bold text-slate-900 mt-1">Verificación fallida</h1>
              </div>
              <div className="text-sm text-slate-600">
                {data?.reason === 'not_found' && 'No se encontró un certificado con este identificador.'}
                {data?.reason === 'invalid_signature' && 'La firma del certificado no coincide.'}
                {data?.reason === 'revoked' && 'Este certificado ha sido revocado.'}
                {state === 'error' && 'No se pudo contactar el servidor de verificación.'}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
